import { Col, Row } from './Grid';
import TextInput from "./TextInput";
import Select from "./Select";
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Label from './Label';
import { useCallback, useMemo } from 'react';
import { readField } from '../utils/objectUtilities';
import useClientConfig from '../hooks/clientConfigHook';

export const toString = ({ value, defaultValue, clientConfig }) => {
    if (!value) {
        return defaultValue;
    }
    const fields = ['address1', 'address2', 'suburb', 'state', 'postcode'];
    const values = fields.map(field => readField(value, field));
    let str = (values[0] ? values[0] + ', ' : '') + (values[1]? `${values[1]} ,` : '') + values.slice(2).join(' ');
    if (clientConfig.address?.number) {
        str = readField(value, 'number') + ' ' + str;
    }
    return str;
}

const states = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
/**
 * A control to capture and display Australian postal addresses.
 * @component
 */
const AddressInputAU = ({address, live, placeholder, hideLabel = false, onChange, readonly, required, disabled : disabledInput, ...props}) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const disabled = useCallback((field) => {
        if (typeof disabledInput === 'function') {
            return disabledInput(field);
        } else {
            return !!disabledInput;
        }
    }, [disabledInput]);

    const readonlyPlaceholder = useMemo(() => placeholder?.default ?? t('address'), [placeholder?.default, t]);
    const addressString = useMemo(() => (clientConfig.address?.number ? address?.number + ' ' : '')
                                        + (address?.address1 ? address.address1 + ', ' : '') 
                                        + (address?.address2 ? address.address2 + ', ' : '') 
                                        + (address?.suburb ? address.suburb + ' ' : '') 
                                        + (address?.state ? address.state + ' ' : '') 
                                        + (address?.postcode ? address.postcode : ''), 
    [clientConfig.address?.number, address?.number, address?.address1, address?.address2, address?.suburb, address?.state, address?.postcode]);

    return (readonly ? <Label placeholder={readonlyPlaceholder} hideLabel={hideLabel} text={addressString} {...props}></Label> : <>
        <Row>
            {clientConfig.address?.number && <Col xs="3"><TextInput live={live} maxLength={20} placeholder={placeholder?.number ?? t('houseNumber')} value={address?.number} hideLabel={hideLabel} onChange={a => onChange('number', a)} required={required} disabled={disabled('number')} {...props}></TextInput></Col> }
            <Col xs={clientConfig.address?.number ? 9 : 12}><TextInput live={live} maxLength={256} placeholder={placeholder?.address1 ?? t('address1')} value={address?.address1} hideLabel={hideLabel} onChange={a => onChange('address1', a)} required={required} disabled={disabled('address1')} {...props}></TextInput></Col>
        </Row>
        <Row>
            <Col><TextInput live={live} maxLength={256} placeholder={placeholder?.address2 ?? t('address2')} value={address?.address2} hideLabel={hideLabel} onChange={a => onChange('address2', a)} disabled={disabled('address2')} {...props}></TextInput></Col>
        </Row>
        <Row>
            <Col md={4}><TextInput live={live} maxLength={256} placeholder={placeholder?.suburb ?? t('suburb')}  value={address?.suburb} hideLabel={hideLabel} onChange={a => onChange('suburb', a)} required={required} disabled={disabled('suburb')} {...props}></TextInput></Col>
            <Col md={4}>
                <Select placeholder={placeholder?.state ?? t('state')} value={address?.state} hideLabel={hideLabel} onChange={a => onChange('state', a)} required={required} options={states} disabled={disabled('state')} {...props}></Select>
            </Col>
            <Col md={4}><TextInput live={live} maxLength={8} placeholder={placeholder?.postcode ?? t('postcode')} value={address?.postcode} hideLabel={hideLabel} onChange={a => onChange('postcode', a)} required={required} disabled={disabled('postcode')} {...props}></TextInput></Col>
        </Row>
        </>)
}

AddressInputAU.propTypes = {
    /**
     * The object in which the address fields should be stored
     */
    address: PropTypes.object,

    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,

    /**
     * A map of placeholders for each of the address components
     */
    placeholder: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.string), PropTypes.oneOf([''])]),  

    /**
     * Supress text labels but leave label spacing
     */
    hidePlaceholders: PropTypes.bool,

    /**
     * Copllapse label spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    readonly: PropTypes.bool,

    /**
     * A function called whenever an address field is updated.
     * The function takes a field name and the new value to be assigned to that field.
     */
    onChange: PropTypes.func,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool
}

export default AddressInputAU;
